import axios from "axios";
import JSZip from "jszip";
import { BpsConfig } from '../entities/formTemplateDataType';
import { message } from '../entities/CxpType';

const zipReportAttachment = async (item: any): Promise<any> => {
    if (!item?.attachment) return item;
    
    const fileName = item.path || 'file';
    const zip = new JSZip();
    const base64Data = item.attachment.includes(',') ? item.attachment.split(',')[1] : item.attachment;
    zip.file(fileName, base64Data, { base64: true });

    const zippedBase64 = await zip.generateAsync({ type: 'base64' });
    const zipFileName = fileName.replace(/\.[^/.]+$/, '') + '.zip';

    return {
        ...item,
        attachment: `data:application/zip;base64,${zippedBase64}`,
        path: zipFileName,
    };
};

export const setMultiFile = async (url, entity: any, config: any) => {
    // if (!url || !entity?.reports || entity.reports.length <= 1) return null;
    const payloads = entity.reports.reduce((sum, item) => {
        sum.push({...entity, reports: [item]})
        return sum;
    }, []);

    const uploadAllPromises = payloads.map(payload => 
        axios.post(url, payload, config)
    );

    const results = await Promise.allSettled(uploadAllPromises);

    return results.map((result, index) => {
        if (result.status === 'fulfilled') {
            return result.value.data; // Success!
        } else {
            console.log(`Upload file ${index} failed:`, result.reason.message);
            return { error: true, index }; // Failed
        }
    });
}

export const setAllFiles = async (url: string, entity: any, config: any) => {
    if (!url || !entity?.reports || !Array.isArray(entity.reports)) return null;
    const zippedReports = await Promise.all(entity.reports.map(zipReportAttachment));
    const payloads = zippedReports.reduce((sum, item) => {
        sum.push({...entity, reports: [item]})
        return sum;
    }, []);

    const requests = payloads.map((payload, index) => 
        axios.post(url, payload, config)
            .then(res => ({ status: res?.status ?? 200, success: true, data: res.data, index }))
            .catch(err => ({
                status: err?.response?.status ?? 500,
                success: false,
                error: err?.response?.data ?? err?.message,
                data: err?.response?.data,
                index
            }))
    );

    try {
        const results = await axios.all(requests) as any[];
        console.log("All file process settled.");
        
        const successful = results.filter(r => r.success);
        const failed = results.filter(r => !r.success);
        console.log(`Success: ${successful.length}, Failed: ${failed.length}`);

        if (failed.length > 0) {
            const firstFailureStatus = failed[0]?.status ?? 500;
            return {
                status: firstFailureStatus >= 400 ? firstFailureStatus : 500,
                data: failed[0]?.data ?? { message: failed[0]?.error || 'One or more file uploads failed' },
                results,
            };
        }

        return {
            status: 200,
            data: successful[0]?.data,
            results,
        };
    } catch (globalError) {
        console.error("A critical error occurred in the batch process:", globalError);
        return {status: 500, success: false, error: globalError.message || 'upload files failed'}
    }
}

export type MultiPartReport = {
    path: string;
    description: string;
    size: number;
    attachment?: ArrayBuffer;
};

export type MultiPartEntity = {
    reports?: MultiPartReport[];
    multiPart?: boolean;         // UI flag — excluded from server payload
    reportDataTable?: any;
    finalReportDataTable?: any;
    language?: string;
    service_id?: string;
    service_name?: string;
};

export type MultiPartUploadProgress = {
    uploaded: number;   // files completed so far
    total: number;      // total number of files
    fileName: string;   // name of the file just processed
    success: boolean;   // whether that file succeeded
};

// Uploads files one by one (up to 50 files, each up to 50 MB) and reports progress after each.
export const uploadMultiPartFiles = async (
    url: string,
    entity: MultiPartEntity,
    config: any,
    onProgress?: (progress: MultiPartUploadProgress) => void,
) => {
    if (!url || !entity?.reports || !Array.isArray(entity.reports)) return null;

    const { reports, multiPart: _flag, ...entityMeta } = entity;
    const files =  reports || [];
    const total = files.length;

    const { headers: { 'Content-Type': _ct, ...restHeaders } = {} as any, ...restConfig } = config ?? {};
    const mergedConfig = { ...restConfig, headers: restHeaders };

    const results: any[] = [];

    for (let index = 0; index < files.length; index++) {
        const { attachment, path, description, size }: MultiPartReport = files[index];
        const fileName = path || `file_${index}`;

        const formData = new FormData();
        formData.append('file', new Blob([attachment]), fileName);
        formData.append('description', description ?? '');
        formData.append('size', String(size ?? ''));
        formData.append('metadata', JSON.stringify(entityMeta));

        try {
            const res = await axios.post(url, formData, mergedConfig);
            results.push({ status: res?.status ?? 200, success: true, data: res.data, index });
            onProgress?.({ uploaded: index + 1, total, fileName, success: true });
        } catch (err: any) {
            results.push({
                status: err?.response?.status ?? 500,
                success: false,
                error: err?.response?.data ?? err?.message,
                data: err?.response?.data,
                index,
            });
            onProgress?.({ uploaded: index + 1, total, fileName, success: false });
        }
    }

    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    console.log(`Multipart upload complete. Success: ${successful.length}, Failed: ${failed.length}`);

    if (failed.length > 0) {
        return {
            status: failed[0].status >= 400 ? failed[0].status : 500,
            data: failed[0].data ?? { message: (failed[0] as any).error || 'One or more file uploads failed' },
            results,
        };
    }

    return {
        status: 200,
        data: successful[0]?.data,
        results,
    };
};

// Uploads all files in parallel using Promise.all and reports progress as each file completes.
export const uploadMultiPartFilesParallel = async (
    url: string,
    entity: MultiPartEntity,
    config: any,
    onProgress?: (progress: MultiPartUploadProgress) => void,
) => {
    if (!url || !entity?.reports || !Array.isArray(entity.reports)) return null;

    const { reports, multiPart: _flag, ...entityMeta } = entity;
    const files = reports || [];
    const total = files.length;

    const { headers: { 'Content-Type': _ct, ...restHeaders } = {} as any, ...restConfig } = config ?? {};
    const mergedConfig = { ...restConfig, headers: restHeaders };

    let uploaded = 0;

    const requests = files.map(({ attachment, path, description, size }, index: number) => {
        const fileName = path || `file_${index}`;

        const formData = new FormData();
        formData.append('file', new Blob([attachment]), fileName);
        formData.append('description', description ?? '');
        formData.append('size', String(size ?? ''));
        formData.append('metadata', JSON.stringify(entityMeta));

        return axios.post(url, formData, mergedConfig)
            .then(res => {
                onProgress?.({ uploaded: ++uploaded, total, fileName, success: true });
                return { status: res?.status ?? 200, success: true, data: res.data, index };
            })
            .catch((err: any) => {
                onProgress?.({ uploaded: ++uploaded, total, fileName, success: false });
                return {
                    status: err?.response?.status ?? 500,
                    success: false,
                    error: err?.response?.data ?? err?.message,
                    data: err?.response?.data,
                    index,
                };
            });
    });

    const results = await Promise.all(requests);

    const successful = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);
    console.log(`Parallel multipart upload complete. Success: ${successful.length}, Failed: ${failed.length}`);

    if (failed.length > 0) {
        return {
            status: failed[0].status >= 400 ? failed[0].status : 500,
            data: failed[0].data ?? { message: (failed[0] as any).error || 'One or more file uploads failed' },
            results,
        };
    }

    return {
        status: 200,
        data: successful[0]?.data,
        results,
    };
};

export const multiPartValidateResponse = (entity: MultiPartEntity) => {
    return entity?.reports?.map(report => ({
        callName: 'CALL-1001',
        errors: [],
        nameOfReport: report.path,
    }));
};

export const isBatchFileLoad = (entity: any) => {
    if (entity?.reports?.length > 1) {
        return !!entity.reports[0].attachment
    }
    return false;
}